import React from "react"
import { PlusOutlined } from "@ant-design/icons"
import {
  Col, Divider,
  Drawer,
  Form,
  Image,
  Input, InputNumber,
  Radio,
  Row,
  Select,
  Tabs,
  Upload,
} from "antd"
import { Button } from "src/components/actions/button"
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg"
import {
  TranslatableInput,
  TranslatableTextarea,
} from "src/components/Translatable"
import { useImage } from "src/hooks/use-image"
import { useAppData } from "src/store/app-data"
import {
  getFormattedLangData,
  getFormData,
  getInitialValues,
  getNestedFormData,
  getOldImage,
} from "src/utils/helpers"
import styled from "styled-components"
import {useUploadFile} from "../../hooks/use-upload-file";
import {API_BASE_MEDIA_URL} from "../../config";

export function EditDrawer({
                             title,
                             initialValues,
                             onClose,
                             onSave,
                           }) {
  const layoutConfig = useAppData((state) => state.layoutConfig)

  const [fileList, setFileList] = React.useState([]);
  const [mobileFileList, setMobileFileList] = React.useState([]);

  const { getImage, imageUpdated, onPreview, onChange, preview } = useImage()

  React.useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(getInitialValues(initialValues))
      if (initialValues.image) {
        const image = initialValues.image;

        setFileList([
          {
            uid: String(image.id),
            name: image.originalName,
            status: "done",
            url: `${API_BASE_MEDIA_URL}${image.path}/${image.systemName}`,
          },
        ]);

        form.setFieldsValue({ fileId: image.id });
      }
      if (initialValues.mobileImage) {
        const mobileImage = initialValues.mobileImage;

        setMobileFileList([
          {
            uid: String(mobileImage.id),
            name: mobileImage.originalName,
            status: "done",
            url: `${API_BASE_MEDIA_URL}${mobileImage.path}/${mobileImage.systemName}`,
          },
        ]);

        form.setFieldsValue({ mobileFileId: mobileImage.id });
      }
    }
  }, [initialValues])

  const [form] = Form.useForm()

  function prepareData(values) {
    const data = {
      ...(initialValues && { _method: "PUT" }),
      link: values.link ?? null,
      isActive: values.isActive ? 1 : 0,
      sortWeight: values.sortWeight ?? 0,
      type: "main",
      fileId: values.fileId,
      ...(values.mobileFileId ? { mobileFileId: values.mobileFileId } : {}),
    }

    return data
  }

  async function onSubmit(values) {
    const baseData = prepareData(values)
    const formData = getFormData({
      values: baseData,
    })

    const data = getFormattedLangData(values)

    const dataWithNestedItem = {
      title: data.title,
      ...((data.description && Array.isArray(data.description)) && {description: data.description})
    }

    const formDataWithNestedFormData = getNestedFormData(
        dataWithNestedItem,
        formData
    )

    try {
      await onSave(formDataWithNestedFormData, initialValues?.id)
      onClose()
      form.resetFields()
    } catch (error) {
      console.log(error)
    }

  }

  function handleClose() {
    onClose()
    form.resetFields()
  }

  const id = initialValues?.id ?? "new"

  const handleBeforeUpload = async (file) => {
    const uploadedFile = await useUploadFile(file);

    if (uploadedFile?.id) {
      const newFile = {
        uid: String(uploadedFile.id),
        url: API_BASE_MEDIA_URL + uploadedFile.path + "/" + uploadedFile.systemName,
        name: file.name,
        status: "done",
      };

      setFileList([newFile]);

      form.setFieldsValue({ fileId: uploadedFile.id });
    }

    return false;
  };

  const handleBeforeUploadMobile = async (file) => {
    const uploadedFile = await useUploadFile(file);

    if (uploadedFile?.id) {
      const newFile = {
        uid: String(uploadedFile.id),
        url: API_BASE_MEDIA_URL + uploadedFile.path + "/" + uploadedFile.systemName,
        name: file.name,
        status: "done",
      };

      setMobileFileList([newFile]);

      form.setFieldsValue({ mobileFileId: uploadedFile.id });
    }

    return false;
  };

  return (
      <DrawerEdit
          width={layoutConfig.isMobile ? "90%" : "80%"}
          title={title}
          onClose={handleClose}
          open
      >
        <FormEdit
            form={form}
            layout={"vertical"}
            onFinish={(values) => onSubmit(values)}
        >
          <StyledFormItem
              name={"image"}
              label={"Обложка"}
              rules={[
                {
                  required: true,
                  message: "Выберите изображение!",
                },
              ]}
          >
            <Upload
                listType="picture-card"
                maxCount={1}
                accept="image/*"
                beforeUpload={handleBeforeUpload}
                fileList={fileList}
                onPreview={onPreview}
                onRemove={() => {
                  setFileList([]);
                  form.setFieldsValue({ fileId: null });
                }}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Загрузить изображение</div>
              </div>
            </Upload>
          </StyledFormItem>

          <StyledFormItem name="fileId" noStyle>
            <Input type="hidden" />
          </StyledFormItem>

          <StyledFormItem
              name={"mobileImage"}
              label={"Обложка для мобильного"}
          >
            <Upload
                listType="picture-card"
                maxCount={1}
                accept="image/*"
                beforeUpload={handleBeforeUploadMobile}
                fileList={mobileFileList}
                onPreview={onPreview}
                onRemove={() => {
                  setMobileFileList([]);
                  form.setFieldsValue({ mobileFileId: null });
                }}
            >
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Загрузить изображение</div>
              </div>
            </Upload>
          </StyledFormItem>

          <StyledFormItem name="mobileFileId" noStyle>
            <Input type="hidden" />
          </StyledFormItem>

          <StyledImage
              alt={preview.previewImage?.name || "Превью"}
              preview={{
                visible: preview.visible,
                onVisibleChange: preview.onVisibleChange,
              }}
              src={preview.previewImage?.largePreview || preview.previewImage?.url}
          />

          <StyledFormItem label={"Заголовок"}>
            <TranslatableInput name={"title"} id={id}></TranslatableInput>
          </StyledFormItem>

          <StyledFormItem label={"Описание"} >
            <TranslatableTextarea id={id} name={"description"} required={false}></TranslatableTextarea>
          </StyledFormItem>

          <StyledFormItem
              key={id + "_sortWeight"}
              label={"Позиция в списке"}
              name={"sortWeight"}
              rules={[
                {
                  required: true,
                  message: "Введите позицию!",
                },
              ]}
          >
            <InputNumber
                min={0}
                style={{
                  width: "100%",
                }}
                placeholder={"Позиция"}
            />
          </StyledFormItem>

          <StyledFormItem
              label={"Ссылка кнопки"}
              key={id + "_link"}
              name={"link"}
              rules={[
                {
                  required: true,
                  message: "Введите адресс ссылки!",
                },
              ]}
          >
            <Input />
          </StyledFormItem>

          <StyledFormItem
              key={id + "_isActive"}
              label={"Включен"}
              name={"isActive"}
              initialValue={form.isActive ? form.isActive : true}
              rules={[
                {
                  required: true,
                  message: "Выберите вариант",
                },
              ]}
          >
            <Radio.Group
                buttonStyle="solid"
                value={form.isActive ? form.isActive : true}
            >
              <Radio.Button value={true}>Да</Radio.Button>
              <Radio.Button value={false}>Нет</Radio.Button>
            </Radio.Group>
          </StyledFormItem>

          <ButtonRow>
            <ButtonCol>
              <Button
                  danger
                  onClick={() => {
                    if (initialValues) {
                      form.resetFields(["image"])
                      form.setFieldsValue(getInitialValues(initialValues))
                      return
                    }
                    form.resetFields()
                  }}
                  icon={<RotateClockwiseSvg />}
              >
                Вернуть
              </Button>
            </ButtonCol>
            <ButtonCol>
              <Button onClick={handleClose} icon={<CloseSvg />}>
                Закрыть
              </Button>
            </ButtonCol>
            <ButtonCol>
              <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                Сохранить
              </Button>
            </ButtonCol>
          </ButtonRow>
        </FormEdit>
      </DrawerEdit>
  )
}

const DrawerEdit = styled(Drawer)``

const FormEdit = styled(Form)``

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`

const StyledTabs = styled(Tabs)`
  overflow: visible;

  &.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    border-bottom: none;
    user-select: none;
  }

  &.ant-tabs-card.ant-tabs-small > .ant-tabs-nav .ant-tabs-tab {
    padding: 3px 7px 6px;
  }

  &.ant-tabs-top > .ant-tabs-nav {
    bottom: -3px;
    margin-bottom: 0;

    &::before {
      border-bottom: none;
    }
  }

  .ant-form-item {
    margin-bottom: 0;
  }
`

const StyledImage = styled(Image)`
  display: none;
`
